import React, { forwardRef, useCallback } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import MediaAPI from "../../../api/MediaAPI";
import { FormProviderWithTranslations } from "../../../contexts/FormTranslationsContext";
import { slugifyInput } from "../../../utils/slug";
import {
  apiToFormTranslations,
  createTranslationResponseToFormField,
  formToApiTranslations,
  getMediaTranslationsFormSchema,
} from "../../../utils/translationAdapters/media";
import Input from "../../form/Input";
import MarkdownEditorField from "../../form/MarkdownEditorField";
import DeletePicturesButton from "../../formTranslations/DeletePicturesButton";
import FormPictures from "../../formTranslations/FormPictures";
import FormTranslations from "../../formTranslations/FormTranslations";
import FormTranslationsHeader from "../../formTranslations/FormTranslationsHeader";

import type {
  GetMediaResponse,
  MediaChildren,
  MediaTranslation,
} from "../../../api/MediaAPI.types";
import type { LanguageCode } from "../../../api/ProjectAPI.types";
import type { PictureKeys } from "../../../constants/ratio";
import type {
  FormTranslationsHandle,
  PageFieldArray,
} from "../../../contexts/FormTranslationsContext";
import type {
  MediaCreateTranslationInput,
  MediaTranslationFormField,
} from "../../../utils/translationAdapters/media";
import type { UploadPictureState } from "../../formTranslations/FormPictures";

export type MediaTranslationsHandle = FormTranslationsHandle<
  ReturnType<typeof formToApiTranslations>
>;

interface MediaTranslationsProps {
  item: GetMediaResponse;
  masterUuid?: string;
}

const MediaTranslations = forwardRef<
  MediaTranslationsHandle,
  MediaTranslationsProps
>(({ item, masterUuid }, ref) => {
  const { t } = useTranslation(["form", "editor-content", "editor-utils"]);

  const queryClient = useQueryClient();

  const getMediaQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMedia,
    params: [item.uuid],
  });

  const methods = useForm<
    PageFieldArray<MediaTranslationFormField>,
    unknown,
    PageFieldArray<MediaTranslationFormField>
  >({
    mode: "onChange",
    resolver: zodResolver(getMediaTranslationsFormSchema()),
    defaultValues: {
      translations: apiToFormTranslations(item.translations),
    },
  });

  const updateChildInMasterCache = useCallback(
    (updater: (child: MediaChildren) => MediaChildren) => {
      if (!masterUuid) return;
      const getMasterMediaQuery = MediaAPI.generateQuery({
        fn: MediaAPI.getMedia,
        params: [masterUuid],
      });
      queryClient.setQueryData<GetMediaResponse>(
        getMasterMediaQuery.queryKey,
        (current) => {
          if (!current) return current;
          return {
            ...current,
            children: current.children?.map((child) =>
              child.uuid === item.uuid ? updater(child) : child,
            ),
          };
        },
      );
    },
    [masterUuid, item.uuid, queryClient],
  );

  const uploadPicture = useMutation({
    mutationFn: async ({
      pictureFile,
      translationId,
      pictureFormat,
    }: {
      pictureFile: File;
      translationId: string;
      pictureFormat: PictureKeys;
    }) =>
      MediaAPI.uploadPicture({
        itemId: item.uuid,
        pictureFile,
        translationId,
        pictureFormat,
      }),
    onSuccess(data, variables) {
      const updatedTranslations = item.translations.map((translation) => {
        if (translation.uuid === variables.translationId) {
          return { ...translation, picture: data.picture };
        }
        return translation;
      });

      const updatedItem = { ...item, translations: updatedTranslations };

      queryClient.setQueryData(getMediaQuery.queryKey, updatedItem);

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, data.picture);
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handlePictureUpload = ({
    file,
    translationId,
    pictureFormat,
  }: {
    file: File;
    translationId: string;
    pictureFormat: PictureKeys;
  }): void => {
    uploadPicture.mutate({ pictureFile: file, translationId, pictureFormat });
  };

  const uploadPictureState: UploadPictureState = {
    isLoading: uploadPicture.isLoading,
    pictureFormat: uploadPicture.variables?.pictureFormat,
    translationId: uploadPicture.variables?.translationId,
  };

  const deletePicture = useMutation({
    mutationFn: async ({ translationId }: { translationId: string }) =>
      MediaAPI.deletePicture({ itemId: item.uuid, translationId }),
    onSuccess(_data, variables) {
      const updatedTranslations = item.translations.map((translation) =>
        translation.uuid === variables.translationId
          ? { ...translation, picture: null }
          : translation,
      );

      queryClient.setQueryData(getMediaQuery.queryKey, {
        ...item,
        translations: updatedTranslations,
      });

      const formTranslations = methods.getValues("translations");
      const formIndex = formTranslations.findIndex(
        (translation) => translation.uuid === variables.translationId,
      );

      if (formIndex >= 0) {
        methods.setValue(`translations.${formIndex}.picture`, undefined);
      }

      toast.success(t("form:deletePicturesSuccess"));
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleAddLanguage = async ({
    languageCode,
    languageUuid,
  }: {
    languageCode: LanguageCode;
    languageUuid: string;
  }): Promise<MediaCreateTranslationInput | null> => {
    if (!languageUuid) return null;

    try {
      const response = await MediaAPI.createTranslation({
        mediaId: item.uuid,
        languageId: languageUuid,
      });

      const augmentedResponse: MediaCreateTranslationInput = {
        uuid: response.uuid,
        language_uuid: languageUuid,
      };

      const newTranslation: MediaTranslation = {
        uuid: response.uuid,
        default: false,
        language: languageCode,
        language_uuid: languageUuid,
        name: "",
        slug: "",
        description: "",
        short_description: "",
        picture: null,
        pictures: null,
        temp_pictures: null,
      };

      if (masterUuid) {
        updateChildInMasterCache((child) => ({
          ...child,
          translations: [...(child.translations ?? []), newTranslation],
        }));
      } else {
        queryClient.setQueryData(getMediaQuery.queryKey, {
          ...item,
          translations: [...item.translations, newTranslation],
        });
      }

      return augmentedResponse;
    } catch (error) {
      console.error("Error creating translation:", error);
      toast.error(t("genericError", { ns: "error" }));
      return null;
    }
  };

  const updateMediaMutation = useMutation({
    mutationFn: async ({ data }: { data: GetMediaResponse }) =>
      MediaAPI.updateMedia({ uuid: item.uuid, data }),
    onSuccess: (_, variables) => {
      toast.success(t("form:deleteLanguageSuccess", { ns: "form" }));

      if (masterUuid) {
        updateChildInMasterCache((child) => ({
          ...child,
          translations: variables.data.translations,
          translations_indexes: variables.data.translations_indexes,
        }));
      } else {
        queryClient.setQueryData(getMediaQuery.queryKey, variables.data);
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleDeleteLanguage = async (
    updatedFormTranslations: ReturnType<typeof apiToFormTranslations>,
  ): Promise<void> => {
    const apiTranslations = formToApiTranslations(updatedFormTranslations);

    await updateMediaMutation.mutateAsync({
      data: {
        ...item,
        translations:
          apiTranslations.translations as unknown as MediaTranslation[],
        translations_indexes: apiTranslations.translations_indexes,
      },
    });
  };

  return (
    <div className="bg-card flex flex-col rounded-[0.3125rem]">
      <FormProviderWithTranslations
        formToApiTranslations={formToApiTranslations}
        handleRef={ref}
        methods={methods}
        onAddLanguage={handleAddLanguage}
        onDeleteLanguage={handleDeleteLanguage}
        toFormField={createTranslationResponseToFormField}
      >
        <FormTranslationsHeader />
        <FormTranslations slugController="name">
          <Input
            isFullWidth
            isRequired
            label={t("fields.title")}
            name="name"
            type="text"
          />
          <Input
            isFullWidth
            label={t("fields.slug")}
            name="slug"
            normalize={slugifyInput}
            prefix={`https://${t("editor-utils:yourProjectUrl")}/content/`}
            type="text"
          />
          <MarkdownEditorField
            isFullWidth
            label={t("editor-content:shortDescription")}
            name="short_description"
          />
          <MarkdownEditorField
            isFullWidth
            label={t("editor-content:description")}
            name="description"
          />
        </FormTranslations>
        <div>
          <div className="mb-0 flex items-center gap-[0.625rem]">
            <div className="font-semibold whitespace-nowrap md:whitespace-normal">
              {t("form:fields.pictures")} ({t("form:fields.picturesLimitation")}
              )
            </div>
            <DeletePicturesButton onDelete={deletePicture.mutateAsync} />
          </div>
          <FormPictures
            item={item}
            itemType="medias"
            onUpload={handlePictureUpload}
            uploadPictureState={uploadPictureState}
          />
        </div>
      </FormProviderWithTranslations>
    </div>
  );
});

MediaTranslations.displayName = "MediaTranslations";

export default MediaTranslations;
